import React, { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import withBreadcrumbs from 'react-router-breadcrumbs-hoc'
import { useIntl } from 'react-intl'
import { Skeleton } from 'antd'
import { useSelector } from 'react-redux'
import { isEmpty, isEqual } from 'lodash-es'
import { parse } from 'query-string'
import financeBankRoutes from 'containers/FinanceBanks/routes'
import useTerm from 'utils/hooks/useTerm'
import {
  backButtonSelector,
  bankAccountsSelector,
  billingSelector,
  darkModeSelector,
  financeConfigSelector,
  loadedInitSelector,
  locationSelector,
  menuLeftDataSelector,
  mobileViewSelector,
  paymentConnectProvidersSelector,
  translationSelector,
} from 'app-redux/selectors'
import { useWarehouseNameResolver } from 'utils/queries/warehouses'
import { decodeUriWithAtob, encodeUriWithBtoa } from 'utils/helper'
import { transType } from 'utils/static'
import { statusConst } from 'containers/FinanceInvoices/static'
import routeConfig from '../../../routes'
import styles from './style.module.scss'
import { Back } from '@/components/UI'

const Breadcrumbs = ({ breadcrumbs, account }) => {
  const { formatMessage } = useIntl()
  const loadedInit = useSelector(loadedInitSelector)
  const location = useSelector(locationSelector)
  const billing = useSelector(billingSelector)
  const menuLeftData = useSelector(menuLeftDataSelector)
  const accounts = useSelector(bankAccountsSelector)
  const paymentConnectProviders = useSelector(paymentConnectProvidersSelector)
  const { menu = {}, reportmenu = {} } = useSelector(translationSelector)
  const isMobileView = useSelector(mobileViewSelector)
  const darkMode = useSelector(darkModeSelector)
  const config = useSelector(financeConfigSelector)

  const _term = useTerm()

  const getWarehouseName = useWarehouseNameResolver()

  const getAccountName = useCallback(
    (id) => {
      let name = ''
      if (id) {
        const findAccount = accounts.find((row) => row.id === id)
        name = findAccount ? findAccount.name : ''
      }

      return name
    },
    [accounts],
  )

  const getProcessActiveKeySettingMenu = useCallback(
    (originalUrl) => {
      const searchs = parse(location.search, { arrayFormat: 'bracket', parseNumbers: true })

      const activeKey = searchs?.activeKey

      return `${originalUrl}${originalUrl.includes('?') ? '&' : '?'}activeKey=${activeKey}`
    },
    [location.search],
  )

  // Function to get additional breadcrumb based on query parameters
  // const getAdditionalBreadcrumb = useCallback(() => {
  //   const params = decodeUriWithAtob(location.search)
  //
  //   if (
  //     location.pathname === '/sales/invoices' &&
  //     params.trans_type_ids === transType.INVOICE_RECAP
  //   ) {
  //     return {
  //       label: menu.zap_invoice_recap,
  //       url: location.pathname + location.search,
  //     }
  //   }
  //
  //   if (
  //     location.pathname === '/sales/deliveries' &&
  //     params.trans_type_ids === transType.DELIVERY_RECAP
  //   ) {
  //     return {
  //       label: menu.zap_delivery_recap,
  //       url: location.pathname + location.search,
  //     }
  //   }
  //
  //   return null
  // }, [location.pathname, location.search, menu.zap_invoice_recap, menu.zap_delivery_recap])

  // Function to get additional breadcrumbs based on query parameters
  const getAdditionalBreadcrumbs = useCallback(() => {
    const params = decodeUriWithAtob(location.search)
    const additionalBreadcrumbs = []

    if (location.pathname === '/sales/invoices') {
      const invoiceStatus = config.invoice_status
      const newSearch = encodeUriWithBtoa({ ...params, status_id: '', due: '' })

      if (params.trans_type_ids === transType.JOIN_INVOICE) {
        additionalBreadcrumbs.push({
          id: 'trans_type_join',
          label: formatMessage({ id: 'financeInvoices.combined_invoice' }),
          url: `${location.pathname}?filters=${newSearch}`,
        })
      }

      if (params.trans_type_ids === transType.PROFORMA_INVOICE) {
        additionalBreadcrumbs.push({
          id: 'trans_type_proforma',
          label: formatMessage({ id: 'financeInvoices.proforma_invoice' }),
          url: `${location.pathname}?filters=${newSearch}`,
        })
      }

      let findInvoiceStatus = invoiceStatus?.find((status) => status.id === params.status_id)

      if (isEqual(params.status_id, [statusConst.UNPAID, statusConst.PARTIAL])) {
        findInvoiceStatus = {
          id: [statusConst.UNPAID, statusConst.PARTIAL].join(','),
          name: formatMessage({ id: 'report.not_yet_paid_off' }),
        }
      }
      if (params.due) {
        findInvoiceStatus = {
          id: 'due',
          name: formatMessage({ id: 'dashboards.overdue' }),
        }
      }
      if (params.status_id === 'retur') {
        findInvoiceStatus = {
          id: 'retur',
          name: formatMessage({ id: 'return.return' }),
        }
      }
      if (params.status_id === 'recurring') {
        findInvoiceStatus = {
          id: 'recurring',
          name: formatMessage({ id: 'recurring.recurring' }),
        }
      }

      if (findInvoiceStatus) {
        additionalBreadcrumbs.push({
          id: `status_${findInvoiceStatus.id}`,
          label: findInvoiceStatus.name,
          url: location.pathname + location.search,
        })
      }
    } else if (location.pathname === '/purchases/purchaseinvoices') {
      const statuses = config.purchaseinvoice_status

      let findInvoiceStatus = statuses?.find((status) => status.id === params.status_id)

      if (isEqual(params.status_id, [statusConst.UNPAID, statusConst.PARTIAL])) {
        findInvoiceStatus = {
          id: [statusConst.UNPAID, statusConst.PARTIAL].join(','),
          name: formatMessage({ id: 'report.not_yet_paid_off' }),
        }
      }
      if (params.due) {
        findInvoiceStatus = {
          id: 'due',
          name: formatMessage({ id: 'dashboards.overdue' }),
        }
      }
      if (params.status_id === 'retur') {
        findInvoiceStatus = {
          id: 'retur',
          name: formatMessage({ id: 'return.return' }),
        }
      }
      if (params.status_id === 'recurring') {
        findInvoiceStatus = {
          id: 'recurring',
          name: formatMessage({ id: 'recurring.recurring' }),
        }
      }

      if (findInvoiceStatus) {
        additionalBreadcrumbs.push({
          id: `status_${findInvoiceStatus.id}`,
          label: findInvoiceStatus.name,
          url: location.pathname + location.search,
        })
      }
    }

    return additionalBreadcrumbs
  }, [
    config.invoice_status,
    config.purchaseinvoice_status,
    formatMessage,
    location.pathname,
    location.search,
  ])

  const renderBreadcrumbs = useMemo(() => {
    if (isMobileView || !loadedInit) {
      return null
    }

    const additionalBreadcrumbs = getAdditionalBreadcrumbs()
    const hasAdditionalBreadcrumbs = additionalBreadcrumbs.length > 0

    // Create extended breadcrumbs array if there are additional breadcrumbs
    const extendedBreadcrumbs = hasAdditionalBreadcrumbs
      ? [
          ...breadcrumbs,
          ...additionalBreadcrumbs.map((additionalBreadcrumb) => ({
            match: {
              url: additionalBreadcrumb.url,
              path: location.pathname,
            },
            breadcrumb: additionalBreadcrumb.label,
            additionalBreadcrumbId: additionalBreadcrumb.id,
            location,
          })),
        ]
      : breadcrumbs

    const countBreadCrumb = extendedBreadcrumbs.length
    const arrMenuLeft = []
    menuLeftData.forEach((data) => {
      if (!isEmpty(data.children)) {
        data.children.forEach((chil) => arrMenuLeft.push(chil))
      }
      const newData = { ...data }
      delete newData.children
      arrMenuLeft.push(newData)
    })

    return extendedBreadcrumbs
      .map((bread, index) => {
        const { match, breadcrumb, additionalBreadcrumbId } = bread
        const findRoute = arrMenuLeft.find((v) => v.url === match.url)
        let link = null
        const isLastBreadcrumb = index === extendedBreadcrumbs.length - 1

        if (isLastBreadcrumb) {
          link = <strong>{breadcrumb}</strong>
        }
        if (typeof breadcrumb.type === 'object') {
          link = breadcrumb
        }

        if (findRoute) {
          if (countBreadCrumb > 1 && !isLastBreadcrumb) {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {findRoute.title}
              </Link>
            )
          } else {
            link = <strong>{findRoute.title}</strong>
          }
        }

        if (match.url === '/sales') {
          link = (
            <Link key={match.url} to={`${match.url}/overview`} className="text-muted">
              {menu.sales}
            </Link>
          )
        }
        if (match.url === '/sales/invoices') {
          // If there are additional breadcrumbs, make this a link instead of strong
          if (hasAdditionalBreadcrumbs) {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {menu.invoices}
              </Link>
            )
          } else if (isLastBreadcrumb) {
            link = <strong>{menu.invoices}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {menu.invoices}
              </Link>
            )
          }
        }
        if (match.url === '/sales/quotes') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{menu.quotes}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {menu.quotes}
              </Link>
            )
          }
        }
        if (match.url === '/sales/orders') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{menu.orders}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {menu.orders}
              </Link>
            )
          }
        }
        if (match.url === '/sales/deliveries') {
          // If there are additional breadcrumbs, make this a link instead of strong
          if (hasAdditionalBreadcrumbs) {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {menu.deliveries}
              </Link>
            )
          } else if (isLastBreadcrumb) {
            link = <strong>{menu.deliveries}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {menu.deliveries}
              </Link>
            )
          }
        }
        if (match.url === '/sales/overview') {
          link = <strong>{menu.overview}</strong>
        }
        if (match.url === '/sales/returns') {
          link = (
            <Link key={match.url} to="/sales/invoices?status_id=retur" className="text-muted">
              {menu.returns}
            </Link>
          )
        }

        if (match.url === '/purchases') {
          link = (
            <Link key={match.url} to={`${match.url}/overview`} className="text-muted">
              {menu.purchases}
            </Link>
          )
        }
        if (match.url === '/purchases/purchaseinvoices') {
          // If there are additional breadcrumbs, make this a link instead of strong
          if (hasAdditionalBreadcrumbs) {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {menu.purchase_invoices}
              </Link>
            )
          } else if (isLastBreadcrumb) {
            link = <strong>{menu.purchase_invoices}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {menu.purchase_invoices}
              </Link>
            )
          }
        }
        if (match.url === '/purchases/purchaseorders') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{menu.purchase_orders}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {menu.purchase_orders}
              </Link>
            )
          }
        }
        if (match.url === '/purchases/quotes') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{menu.purchase_quotes}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {menu.purchase_quotes}
              </Link>
            )
          }
        }
        if (match.url === '/purchases/deliveries') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{menu.purchase_deliveries}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {menu.purchase_deliveries}
              </Link>
            )
          }
        }
        if (match.url === '/purchases/overview') {
          link = <strong>{menu.overview}</strong>
        }
        if (match.url === '/purchases/returns') {
          link = (
            <Link
              key={match.url}
              to="/purchases/purchaseinvoices?status_id=retur"
              className="text-muted"
            >
              {menu.purchase_returns}
            </Link>
          )
        }

        if (match.url === '/membership') {
          link = (
            <span key={match.url} className="text-muted">
              {menu.membership || 'Membership'}
            </span>
          )
        }

        if (
          /^\/membership\/member\/detail\/[^/]+$/.test(match.url) &&
          index === breadcrumbs.length - 1
        ) {
          link = <strong>{formatMessage({ id: 'breadcrumb.detail' })}</strong>
        }

        if (match.url === '/membership/member/points') {
          link = <strong>{formatMessage({ id: 'membership.point_movement' })}</strong>
        }

        if (match.url === '/marketplace') {
          link = (
            <Link key={match.url} to={`${match.url}/overview`} className="text-muted">
              {formatMessage({ id: 'marketplaceConnect.marketplace' })}
            </Link>
          )
        }
        if (match.url === '/marketplace/overview') {
          link = <strong>{menu.overview || 'Overview'}</strong>
        }
        if (match.url === '/marketplace/marketplace-products') {
          if (isLastBreadcrumb) {
            link = (
              <strong>{formatMessage({ id: 'marketplace.marketplace_products_title' })}</strong>
            )
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {formatMessage({ id: 'marketplace.marketplace_products_title' })}
              </Link>
            )
          }
        }
        if (match.url === '/marketplace/marketplace-products/bump-management') {
          if (isLastBreadcrumb) {
            link = <strong>{formatMessage({ id: 'marketplace.boost_settings_title' })}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {formatMessage({ id: 'marketplace.boost_settings_title' })}
              </Link>
            )
          }
        }
        if (match.url === '/marketplace/my-marketplace') {
          link = <strong>{formatMessage({ id: 'marketplace.my_marketplace_title' })}</strong>
        }

        if (
          /\/marketplace\/marketplace-products\/edit\/[^/]+\/[^/]+$/.test(match.url) ||
          /\/marketplace\/marketplace-products\/edit\/[^/]+$/.test(match.url)
        ) {
          link = null
        }

        if (match.url === '/marketplace/marketplace-products/detail') {
          link = <strong>{formatMessage({ id: 'breadcrumb.detail' })}</strong>
        }

        if (
          /\/marketplace\/marketplace-products\/detail\/[^/]+\/[^/]+$/.test(match.url) ||
          /\/marketplace\/marketplace-products\/detail\/[^/]+$/.test(match.url)
        ) {
          link = null
        }

        if (match.url === '/pos') {
          link = (
            <span key={match.url} className="text-muted">
              {menu.pos}
            </span>
          )
        }
        if (match.url === '/pos/product-favorites') {
          link = <strong>{menu.pos_product_favorites}</strong>
        }
        if (match.url === '/pos/users') {
          link = <strong>{menu.pos_cashier_users}</strong>
        }
        if (match.url === '/pos/outlets' && index === breadcrumbs.length - 1) {
          link = <strong>{menu.pos_outlets}</strong>
        }
        if (match.url === '/pos/settings/pos-settings') {
          link = <strong>{formatMessage({ id: 'kledoPos.setting' })}</strong>
        }
        if (match.url === '/pos/settings/driver-online') {
          link = <strong>{formatMessage({ id: 'kledoPos.settings_driver_online' })}</strong>
        }

        if (match.url === '/accounts/conversionbalances/conversiondate') {
          link = <strong>{formatMessage({ id: 'breadcrumb.conversiondate' })}</strong>
        }
        if (match.url === '/accounts/conversionbalances') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{formatMessage({ id: 'breadcrumb.conversionbalances' })}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {formatMessage({ id: 'breadcrumb.conversionbalances' })}
              </Link>
            )
          }
        }

        if (match.url === '/dashboard') {
          link = <strong>{menu.home}</strong>
        }

        if (match.url === '/settings/billing/upgrade') {
          link = (
            <strong>
              {billing.can_be_renewed && location.query.isRenew === '1'
                ? formatMessage({ id: 'billing.renew' })
                : formatMessage({ id: 'billing.upgrade' })}
            </strong>
          )
        }
        if (match.url === '/settings/billing/upgrade-all') {
          link = <strong>{formatMessage({ id: 'billing.upgrade' })}</strong>
        }
        if (match.url === '/billing') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{menu.billing}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {menu.billing}
              </Link>
            )
          }
        }

        if (match.url === '/reports') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{menu.reports}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {menu.reports}
              </Link>
            )
          }
        }

        if (match.url === '/companies') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{formatMessage({ id: 'companies.company_list' })}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {formatMessage({ id: 'companies.company_list' })}
              </Link>
            )
          }
        }

        if (match.url === '/settings') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{menu.settings}</strong>
          } else {
            link = (
              <Link
                key={match.url}
                to={getProcessActiveKeySettingMenu(match.url)}
                className="text-muted"
              >
                {menu.settings}
              </Link>
            )
          }
        }
        if (match.url === '/settings/account-mapping') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{formatMessage({ id: 'accountMapping.account_mappinng' })}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {formatMessage({ id: 'accountMapping.account_mappinng' })}
              </Link>
            )
          }
        }
        if (match.url === '/settings/apps') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{menu.apps}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {menu.apps}
              </Link>
            )
          }
        }
        if (match.url === '/settings/multi-currency') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{formatMessage({ id: 'multiCurrency.multi_currency' })}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {formatMessage({ id: 'multiCurrency.multi_currency' })}
              </Link>
            )
          }
        }
        if (match.url === '/settings/price-forecast') {
          link = <strong>{menu.price_forecast}</strong>
        }
        if (match.url === '/settings/shopify-connect') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{menu.shopify_connect}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {menu.shopify_connect}
              </Link>
            )
          }
        }
        if (match.url === '/settings/marketplaceConnect') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>Marketplace Connect</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                Marketplace Connect
              </Link>
            )
          }
        }
        if (match.url === '/settings/sales-commissions') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{formatMessage({ id: 'salesCommission.sales_commission' })}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {formatMessage({ id: 'salesCommission.sales_commission' })}
              </Link>
            )
          }
        }

        if (match.url === '/reports/sales-per-period') {
          link = <strong>{reportmenu.sales_per_period}</strong>
        }
        if (match.url === '/reports/purchases-per-period') {
          link = <strong>{reportmenu.purchases_per_period}</strong>
        }
        if (match.url === '/reports/receivable-payable-per-contact') {
          link = <strong>{reportmenu.payable_credit_per_contact}</strong>
        }
        if (match.url === '/accounts/conversionbalances/opening_balance') {
          link = <strong>{formatMessage({ id: 'financeConversion.opening_balance' })}</strong>
        }
        if (match.url === '/accounts/conversionbalances/reversal_detail') {
          link = <strong>{formatMessage({ id: 'financeConversion.reversal_detail' })}</strong>
        }
        if (match.url === '/reports/inventory-summary') {
          link = <strong>{reportmenu.inventory_summary}</strong>
        }
        if (match.url === '/reports/inventory-stock-movement') {
          link = <strong>{reportmenu.inventory_stock_movement}</strong>
        }
        if (match.url === '/reports/product-stock-movement') {
          link = <strong>{reportmenu.product_stock_movement}</strong>
        }
        if (match.url === '/reports/warehouse-stock-summary') {
          link = <strong>{reportmenu.warehouse_stock_summary}</strong>
        }
        if (match.url === '/reports/warehouse-stock-movement') {
          link = <strong>{reportmenu.warehouse_stock_movement}</strong>
        }
        if (match.url === '/reports/inventory-stock-adjustment') {
          link = <strong>{reportmenu.report_inventory_stock_adjustment}</strong>
        }
        if (match.url === '/reports/low-stock') {
          link = <strong>{reportmenu.low_stock}</strong>
        }
        if (match.url === '/reports/warehouse-product-quota') {
          link = <strong>{reportmenu.warehouse_product_quota}</strong>
        }
        if (match.url === '/reports/warehouse-transfer') {
          link = <strong>{reportmenu.inventory_warehouse_transfer}</strong>
        }
        if (match.url === '/reports/production') {
          link = <strong>{reportmenu.production}</strong>
        }
        if (match.url === '/reports/production-audit') {
          link = <strong>{reportmenu.production_audit}</strong>
        }
        if (match.url === '/reports/material-request-fulfillment') {
          link = <strong>{reportmenu.material_request_fulfillment}</strong>
        }
        if (match.url === '/reports/material-usage-history') {
          link = <strong>{reportmenu.material_usage_history}</strong>
        }
        if (match.url === '/reports/material-usage-per-project') {
          link = <strong>{reportmenu.material_usage_per_project}</strong>
        }
        if (match.url === '/reports/shipping-cost-per-expedition') {
          link = <strong>{reportmenu.shipping_cost_per_expedition}</strong>
        }
        if (match.url === '/reports/sales-per-sales-person') {
          link = <strong>{reportmenu.sales_per_sales_person}</strong>
        }
        if (match.url === '/reports/product-profitability') {
          link = <strong>{reportmenu.profitability_product}</strong>
        }
        if (match.url === '/reports/profitability-per-invoice') {
          link = <strong>{reportmenu.profitability_per_invoice}</strong>
        }
        if (match.url === '/reports/invoice-payment') {
          link = <strong>{reportmenu.invoice_payment}</strong>
        }
        if (match.url === '/reports/customer-payment-recap') {
          link = <strong>{reportmenu.customer_payment_recap}</strong>
        }
        if (match.url === '/reports/product-sales-per-customer') {
          link = <strong>{reportmenu?.product_sales_per_customer}</strong>
        }
        if (match.url === '/reports/sales-per-product-category') {
          link = <strong>{reportmenu.sales_per_product_category}</strong>
        }
        if (match.url === '/reports/product-purchases-per-vendor') {
          link = <strong>{reportmenu?.product_purchases_per_supplier}</strong>
        }
        if (match.url === '/reports/purchase-invoice-payment') {
          link = <strong>{reportmenu.purchases_invoice_payment}</strong>
        }
        if (match.url === '/reports/general-ledger') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{reportmenu.general_ledger}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {reportmenu.general_ledger}
              </Link>
            )
          }
        }
        if (match.url === '/reports/general-ledger/category') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{formatMessage({ id: 'report.category' })}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {formatMessage({ id: 'report.category' })}
              </Link>
            )
          }
        }
        if (match.url === '/reports/subsidiary-ledger') {
          link = <strong>{reportmenu.subsidiary_ledger}</strong>
        }
        if (match.url === '/reports/journal') {
          link = <strong>{reportmenu.journal}</strong>
        }
        if (match.url === '/reports/bank-summary') {
          link = <strong>{reportmenu.bank_summary}</strong>
        }
        if (match.url === '/reports/sales-tax') {
          link = <strong>{reportmenu.sales_tax}</strong>
        }
        if (match.url === '/reports/witholding-tax') {
          link = <strong>{reportmenu.witholding_tax}</strong>
        }
        if (match.url === '/reports/customer-invoice') {
          link = <strong>{reportmenu.customer_invoice}</strong>
        }
        if (match.url === '/reports/supplier-invoice') {
          link = <strong>{reportmenu.supplier_invoice}</strong>
        }
        if (match.url === '/reports/sales-detail') {
          link = <strong>{reportmenu.sales_detail}</strong>
        }
        if (match.url === '/reports/purchase-detail') {
          link = <strong>{reportmenu.purchase_detail}</strong>
        }
        if (match.url === '/reports/aged-payable') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{reportmenu.aged_payables}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {reportmenu.aged_payables}
              </Link>
            )
          }
        }
        if (match.url === '/reports/aged-receivable') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{reportmenu.aged_receivables}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {reportmenu.aged_receivables}
              </Link>
            )
          }
        }
        if (match.url === '/reports/customer-receivable-snapshot') {
          const name =
            reportmenu.customer_receivable_snapshot ||
            formatMessage({ id: 'report.customer_receivable_snapshot' })
          if (index === breadcrumbs.length - 1) {
            link = <strong>{name}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {name}
              </Link>
            )
          }
        }
        if (match.url === '/reports/customer-debt-snapshot') {
          const name =
            reportmenu.customer_debt_snapshot ||
            formatMessage({ id: 'report.customer_debt_snapshot' })
          if (index === breadcrumbs.length - 1) {
            link = <strong>{name}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {name}
              </Link>
            )
          }
        }
        if (match.url === '/reports/expense-claim') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{reportmenu.expense_claim_detail}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {reportmenu.expense_claim_detail}
              </Link>
            )
          }
        }

        if (match.url === '/reports/pos-shift') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{reportmenu.pos_shift}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {reportmenu.pos_shift}
              </Link>
            )
          }
        }

        if (match.url === '/reports/pos-payment-type') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{reportmenu.pos_sales_by_payment_type}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {reportmenu.pos_sales_by_payment_type}
              </Link>
            )
          }
        }

        if (match.url === '/reports/pos-order-type') {
          link = <strong>{reportmenu.pos_sales_by_invoice_type}</strong>
        }

        if (match.url === '/reports/pos-sales-price-rule') {
          if (index === breadcrumbs.length - 1) {
            link = (
              <strong>
                {reportmenu.pos_sales_price_rule ||
                  formatMessage({ id: 'report.pos_sales_price_rule' })}
              </strong>
            )
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {reportmenu.pos_sales_price_rule ||
                  formatMessage({ id: 'report.pos_sales_price_rule' })}
              </Link>
            )
          }
        }

        if (match.url === '/reports/attachment') {
          link = <strong>{reportmenu.attachment}</strong>
        }
        if (match.url === '/reports/export') {
          link = <strong>{reportmenu.export}</strong>
        }
        if (match.url === '/reports/import') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{reportmenu.import}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {reportmenu.import}
              </Link>
            )
          }
        }
        if (match.url === '/reports/team-activities') {
          link = <strong>{reportmenu.activity_team}</strong>
        }

        if (match.url === '/reports/duty-stamp') {
          link = <strong>{reportmenu.duty_stamp}</strong>
        }

        if (match.url === '/sales/invoices/join-invoice') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{formatMessage({ id: 'financeInvoices.join_invoice' })}</strong>
          } else {
            link = (
              <Link
                key={match.url}
                to="/sales/invoices?filters=JTdCJTIydHJhbnNfdHlwZV9pZHMlMjIlM0EyNTAlMkMlMjJwcm9kdWN0X2NhdGVnb3J5X2lkcyUyMiUzQSU1QiU1RCU3RA=="
                className="text-muted"
              >
                {formatMessage({ id: 'financeInvoices.join_invoice' })}
              </Link>
            )
          }
        }

        if (match.url === '/purchases/purchaseinvoices/join-invoice') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{formatMessage({ id: 'financeInvoices.join_purchase_invoice' })}</strong>
          } else {
            link = (
              <Link
                key={match.url}
                to="/purchases/purchaseinvoices?filters=JTdCJTIydHJhbnNfdHlwZV9pZHMlMjIlM0EyNDAlMkMlMjJwcm9kdWN0X2NhdGVnb3J5X2lkcyUyMiUzQSU1QiU1RCU3RA=="
                className="text-muted"
              >
                {formatMessage({ id: 'financeInvoices.join_purchase_invoice' })}
              </Link>
            )
          }
        }

        if (match.url === '/settings/billing/stamp') {
          link = <strong>{formatMessage({ id: 'stamp.billing_title' })}</strong>
        }

        if (match.url === '/settings/billing/whatsapp') {
          link = <strong>{formatMessage({ id: 'billing.whatsapp_title' })}</strong>
        }

        if (
          match.url === '/sales/invoices/recurring' ||
          match.url === '/sales/orders/recurring' ||
          match.url === '/purchases/purchaseinvoices/recurring' ||
          match.url === '/purchases/purchaseorders/recurring' ||
          match.url === '/expenses/recurring' ||
          match.url === '/manual-journal/recurring' ||
          /banks\/[0-9]+\/recurring$/.test(match.url) ||
          /banks\/detail\/[0-9]+\/recurring$/.test(match.url)
        ) {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{formatMessage({ id: 'recurring.recurring' })}</strong>
          } else {
            link = (
              <span className="text-muted">{formatMessage({ id: 'recurring.recurring' })}</span>
            )
          }
        }

        if (match.url === '/reports/income-per-customer') {
          link = <strong>{reportmenu.income_per_customer}</strong>
        }
        if (match.url === '/reports/trial-balance') {
          link = <strong>{reportmenu.trial_balance}</strong>
        }
        if (match.url === '/reports/profit-loss') {
          link = <strong>{reportmenu.profit_and_loss}</strong>
        }
        if (match.url === '/reports/expense-per-contact') {
          link = <strong>{reportmenu.expense_per_contact}</strong>
        }
        if (match.url === '/reports/sales-per-product') {
          link = <strong>{reportmenu.sales_per_product}</strong>
        }
        if (match.url === '/reports/order-per-product') {
          link = <strong>{reportmenu.order_per_product}</strong>
        }
        if (match.url === '/reports/return-per-product') {
          link = <strong>{reportmenu.return_per_product}</strong>
        }
        if (match.url === '/reports/purchase-return-per-product') {
          link = <strong>{reportmenu.purchase_return_per_product}</strong>
        }
        if (match.url === '/reports/purchases-per-vendor') {
          link = <strong>{reportmenu.purchases_per_supplier}</strong>
        }
        if (match.url === '/reports/purchases-per-product') {
          link = <strong>{reportmenu.purchases_per_product}</strong>
        }
        if (match.url === '/reports/cash-flow') {
          link = <strong>{reportmenu.cash_flow}</strong>
        }
        if (match.url === '/reports/cash-flow-projection') {
          link = <strong>{reportmenu.cash_flow_projection}</strong>
        }
        if (match.url === '/reports/balance-sheet') {
          link = <strong>{reportmenu.balance_sheet}</strong>
        }
        if (match.url === '/reports/equity-movement') {
          link = <strong>{reportmenu.equity_movement}</strong>
        }
        if (match.url === '/reports/executive-summary') {
          link = <strong>{reportmenu.executive_summary}</strong>
        }
        if (match.url === '/reports/account-transaction') {
          link = (
            <strong>
              {formatMessage({ id: 'report.account_transactions_title' }, { account: '' })}
            </strong>
          )
        }
        if (match.url === '/reports/fixed-asset-detail') {
          link = <strong>{reportmenu.fixed_asset_detail}</strong>
        }
        if (match.url === '/reports/fixed-asset-summary') {
          link = <strong>{reportmenu.fixed_asset_summary}</strong>
        }
        if (match.url === '/reports/fixed-asset-disposal') {
          link = <strong>{reportmenu.fixed_asset_disposal}</strong>
        }
        if (match.url === '/reports/consolidation-balance-sheet') {
          link = <strong>{`${reportmenu.balance_sheet} ${menu.consolidation}`}</strong>
        }
        if (match.url === '/reports/consolidation-cash-flow') {
          link = <strong>{`${reportmenu.cash_flow} ${menu.consolidation}`}</strong>
        }
        if (match.url === '/reports/consolidation-profit-loss') {
          link = <strong>{`${reportmenu.profit_and_loss} ${menu.consolidation}`}</strong>
        }
        if (match.url === '/reports/consolidation-worksheet') {
          link = <strong>{`${reportmenu.worksheet} ${menu.consolidation}`}</strong>
        }
        if (match.url === '/reports/sales-delivery') {
          link = <strong>{reportmenu.sales_delivery}</strong>
        }
        if (match.url === '/reports/purchase-delivery') {
          link = <strong>{reportmenu.purchase_delivery}</strong>
        }
        if (match.url === '/reports/budget-management') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{reportmenu.budget}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {reportmenu.budget}
              </Link>
            )
          }
        }
        if (match.url === '/reports/budget-profit-loss') {
          link = <strong>{reportmenu.budget_profit_loss}</strong>
        }
        if (match.url === '/manual-journal') {
          if (index === breadcrumbs.length - 1) {
            link = (
              <strong>{formatMessage({ id: 'financeManualJournal.financeManualJournal' })}</strong>
            )
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {formatMessage({ id: 'financeManualJournal.financeManualJournal' })}
              </Link>
            )
          }
        }
        if (match.url === '/closing') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{formatMessage({ id: 'financeClosing.closing' })}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {formatMessage({ id: 'financeClosing.closing' })}
              </Link>
            )
          }
        }
        if (match.url === '/fixed-assets/transaction') {
          link = <strong>{formatMessage({ id: 'financeFixedAssets.transactions' })}</strong>
        }
        if (match.url === '/fixed-assets/run-depreciation') {
          link = <strong>{formatMessage({ id: 'financeFixedAssets.run_depreciation' })}</strong>
        }

        if (match.url === '/layout-invoice') {
          link = <strong>{menu.layout_invoice}</strong>
        }

        if (match.url === '/settings/layout-report') {
          link = <strong>{formatMessage({ id: 'layoutReport.layout_report' })}</strong>
        }

        if (match.url === '/settings/backup-database') {
          link = <strong>{menu.finance_backup_database}</strong>
        }

        const splitUrl = match.url.split('/')
        const lastUrl = splitUrl[splitUrl.length - 1]

        // Bank custom breadcrumbs
        if (/banks\/[0-9]+$/.test(match.url) || /banks\/detail\/[0-9]+$/.test(match.url)) {
          const id = parseInt(lastUrl, 10)
          link = (
            <Link key={match.url} to={`/banks/detail/${id}`} className="text-muted">
              {getAccountName(id)}
            </Link>
          )
        }

        if (/banks\/[0-9]+\/spend$/.test(match.url)) {
          link = <strong>{formatMessage({ id: 'financeBanks.spend_money' })}</strong>
        }

        if (
          /banks\/[0-9]+\/spend\/[0-9]+$/.test(match.url) ||
          /banks\/[0-9]+\/receive\/[0-9]+$/.test(match.url)
        ) {
          link = null
        }

        if (/banks\/[0-9]+\/receive$/.test(match.url)) {
          link = <strong>{formatMessage({ id: 'financeBanks.receive_money' })}</strong>
        }

        if (/banks\/[0-9]+\/multi-invoice-payment$/.test(match.url)) {
          link = (
            <strong>{formatMessage({ id: 'financeBanks.receive_multi_invoice_payment' })}</strong>
          )
        }

        if (/banks\/[0-9]+\/multi-invoice-payment\/[0-9]+$/.test(match.url)) {
          link = null
        }

        if (/banks\/[0-9]+\/multi-purchase-payment$/.test(match.url)) {
          link = (
            <strong>{formatMessage({ id: 'financeBanks.spend_multi_purchase_payment' })}</strong>
          )
        }

        if (/banks\/[0-9]+\/multi-purchase-payment\/[0-9]+$/.test(match.url)) {
          link = null
        }

        if (/banks\/transaction$/.test(match.url)) {
          if (account?.id) {
            link = <span className="text-muted">{account?.name}</span>
          }
        }

        if (/banks\/detail\/[0-9]+\/transactions$/.test(match.url)) {
          link = <strong>{formatMessage({ id: 'financeBanks.transactions' })}</strong>
        }

        if (match.url === '/banks') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{menu.cash_bank}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {menu.cash_bank}
              </Link>
            )
          }
        }

        if (match.url === '/banks/connect') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{formatMessage({ id: 'financeBanks.bank_connect' })}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {formatMessage({ id: 'financeBanks.bank_connect' })}
              </Link>
            )
          }
        }

        // Warehouse Custom Breadcrumbs
        if (/warehouses\/[0-9]+$/.test(match.url)) {
          const id = parseInt(lastUrl, 10)
          link = (
            <Link key={match.url} to={`/warehouses/detail/${id}`} className="text-muted">
              {getWarehouseName(id)}
            </Link>
          )
        }

        if (match.url === '/warehouses/access' || /banks\/[0-9]+\/access$/.test(match.url)) {
          link = (
            <span className="text-muted">{formatMessage({ id: 'financeWareHouses.access' })}</span>
          )
        }

        if (/warehouses\/[0-9]+\/transfer$/.test(match.url)) {
          link = (
            <span className="text-muted">
              {formatMessage({ id: 'financeWareHouses.transfer' })}
            </span>
          )
        }

        if (/warehouses\/transfer-group$/.test(match.url)) {
          link = (
            <span className="text-muted">
              {formatMessage({ id: 'financeWareHouses.warehouse_transfer_group' })}
            </span>
          )
        }

        if (/warehouses\/[0-9]+\/stock-adjustment$/.test(match.url)) {
          link = (
            <span className="text-muted">
              {formatMessage({ id: 'financeWareHouses.stock_adjustment' })}
            </span>
          )
        }

        if (
          /warehouses\/[0-9]+\/transfer\/detail\/[0-9]+$/.test(match.url) ||
          /warehouses\/[0-9]+\/stock-adjustment\/detail\/[0-9]+$/.test(match.url)
        ) {
          link = <strong>{formatMessage({ id: 'breadcrumb.detail' })}</strong>
        }

        if (lastUrl === 'import-recurring') {
          link = (
            <strong>
              {formatMessage({ id: 'button.import' })}{' '}
              {formatMessage({
                id: 'recurring.repeat',
              })}
            </strong>
          )
        }

        if (lastUrl === 'import-return') {
          link = (
            <strong>
              {formatMessage({
                id: 'financeInvoices.import_invoice_return',
              })}
            </strong>
          )
        }

        if (lastUrl === 'transfer-in') {
          link = <strong>{formatMessage({ id: 'financeWareHouses.transfer_in' })}</strong>
        } else if (/warehouses\/[0-9]+\/transfer-in\/[0-9]+$/.test(match.url)) {
          link = ''
        }

        if (lastUrl === 'transfer-out') {
          link = <strong>{formatMessage({ id: 'financeWareHouses.transfer_out' })}</strong>
        } else if (/warehouses\/[0-9]+\/transfer-out\/[0-9]+$/.test(match.url)) {
          link = ''
        }

        if (lastUrl === 'add') {
          link = <strong>{formatMessage({ id: 'breadcrumb.add' })}</strong>
        }

        if (splitUrl[splitUrl.length - 2] === 'add') {
          link = ''
        }

        if (lastUrl === 'edit') {
          link = <strong>{formatMessage({ id: 'breadcrumb.edit' })}</strong>
        }

        if (lastUrl === 'detail' && index === breadcrumbs.length - 1) {
          link = <strong>{formatMessage({ id: 'breadcrumb.detail' })}</strong>
        }

        if (lastUrl === 'email-template') {
          link = <strong>{menu.email_template}</strong>
        }

        if (lastUrl === 'due-reminder') {
          link = <strong>{menu.due_reminder}</strong>
        }

        if (lastUrl === 'taxes') {
          link = <strong>{menu.taxes}</strong>
        }

        if (lastUrl === 'unit') {
          link = <strong>{menu.units}</strong>
        }

        if (lastUrl === 'shipping-company' && index === breadcrumbs.length - 1) {
          link = <strong>{menu.shipping_comps}</strong>
        }

        if (lastUrl === 'expenses' && index === breadcrumbs.length - 1) {
          link = <strong>{menu.expenses}</strong>
        }

        if (lastUrl === 'products' && index === breadcrumbs.length - 1) {
          link = <strong>{menu.products}</strong>
        }

        if (match.url === '/products/production-plans') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{formatMessage({ id: 'productionPlan.title' })}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {formatMessage({ id: 'productionPlan.title' })}
              </Link>
            )
          }
        }

        if (
          index === breadcrumbs.length - 1 &&
          breadcrumbs[breadcrumbs.length - 1].match.path === '/products/access/:id'
        ) {
          link = <strong>{formatMessage({ id: 'financeWareHouses.access' })}</strong>
        }

        if (lastUrl === 'price-rule') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{formatMessage({ id: 'priceRule.price_rule' })}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {formatMessage({ id: 'priceRule.price_rule' })}
              </Link>
            )
          }
        }

        if (lastUrl === 'import-package') {
          if (index === breadcrumbs.length - 1) {
            link = (
              <strong>
                {`${formatMessage({ id: 'button.import' })} ${formatMessage({
                  id: 'financeProduct.package',
                })}`}
              </strong>
            )
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {`${formatMessage({ id: 'button.import' })} ${formatMessage({
                  id: 'financeProduct.package',
                })}`}
              </Link>
            )
          }
        }

        if (lastUrl === 'import-manufacture') {
          if (index === breadcrumbs.length - 1) {
            link = (
              <strong>
                {`${formatMessage({ id: 'button.import' })} ${formatMessage({
                  id: 'financeProduct.manufacture',
                })}`}
              </strong>
            )
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {`${formatMessage({ id: 'button.import' })} ${formatMessage({
                  id: 'financeProduct.manufacture',
                })}`}
              </Link>
            )
          }
        }

        if (lastUrl === 'import-conversion') {
          if (index === breadcrumbs.length - 1) {
            link = (
              <strong>
                {`${formatMessage({ id: 'button.import' })} ${formatMessage({
                  id: 'financeProduct.conversion',
                })}`}
              </strong>
            )
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {`${formatMessage({ id: 'button.import' })} ${formatMessage({
                  id: 'financeProduct.conversion',
                })}`}
              </Link>
            )
          }
        }

        if (lastUrl === 'import-conversion-unit') {
          link = (
            <strong>
              {formatMessage({ id: 'button.import' })}{' '}
              {formatMessage({ id: 'financeProduct.unit_conversion' })}
            </strong>
          )
        }

        if (lastUrl === 'import-variant') {
          link = (
            <strong>
              {formatMessage({ id: 'button.import' })} {formatMessage({ id: 'variant' })}
            </strong>
          )
        }

        if (lastUrl === 'conversions' && index === breadcrumbs.length - 1) {
          link = <strong>{formatMessage({ id: 'financeProduct.product_conversion' })}</strong>
        }

        if (lastUrl === 'finance-approval') {
          link = <strong>{menu?.approval}</strong>
        }

        if (lastUrl === 'approval-setting') {
          link = <strong>{menu?.finance_approval_setting}</strong>
        }

        if (lastUrl === 'tags' && index === breadcrumbs.length - 1) {
          link = <strong>{menu.tags}</strong>
        }

        if (match.url.includes('/settings/tags/access')) {
          link = <strong>{formatMessage({ id: 'financeWareHouses.access' })}</strong>
        }

        if (lastUrl === 'contacts' && index === breadcrumbs.length - 1) {
          link = <strong>{menu.contacts}</strong>
        }

        if (lastUrl === 'import-accounts-payable' && index === breadcrumbs.length - 1) {
          link = (
            <strong>
              {formatMessage(
                { id: 'financeContacts.import_contact_transactions_with_title' },
                { credit: _term.payable, debit: _term.receivable },
              )}
            </strong>
          )
        }

        if (lastUrl === 'import-payment-account-payable' && index === breadcrumbs.length - 1) {
          link = (
            <strong>
              {formatMessage(
                { id: 'financeContacts.import_contact_payment_transactions' },
                { credit: _term.payable, debit: _term.receivable },
              )}
            </strong>
          )
        }

        if (lastUrl === 'accounts' && index === breadcrumbs.length - 1) {
          link = <strong>{menu.accounts}</strong>
        }

        if (lastUrl === 'warehouses' && index === breadcrumbs.length - 1) {
          link = <strong>{menu.inventory}</strong>
        }

        if (lastUrl === 'company') {
          link = <strong>{menu.company}</strong>
        }

        if (lastUrl === 'payments') {
          link = <strong>{formatMessage({ id: 'payments.payment_method' })}</strong>
        }

        if (lastUrl === 'custom-column') {
          link = <strong>{menu.custom_columns}</strong>
        }

        if (lastUrl === 'terms') {
          link = <strong>{menu.terms}</strong>
        }

        if (lastUrl === 'business-flow') {
          link = <strong>{menu.businessflow}</strong>
        }

        if (lastUrl === 'customer-birthday') {
          link = <strong>{menu.customer_birthday}</strong>
        }

        if (lastUrl === 'automatic-sequencing') {
          link = <strong>{menu.automatic_sequencing}</strong>
        }

        if (lastUrl === 'users' && match.url !== '/pos/users') {
          link = <strong>{menu.users}</strong>
        }

        if (lastUrl === 'roles' && index === breadcrumbs.length - 1) {
          link = <strong>{menu.roles}</strong>
        }

        if (match.url === '/profile') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{menu.my_profile}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                Test
              </Link>
            )
          }
        }

        if (lastUrl === 'consolidation') {
          link = <strong>{menu.consolidation}</strong>
        }

        if (match.url === '/setup') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{menu.setup}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {menu.setup}
              </Link>
            )
          }
        }

        if (lastUrl === 'clear-demo-data') {
          link = <strong>{formatMessage({ id: 'setup.clear_demo_data' })}</strong>
        }

        if (lastUrl === 'add-room-types') {
          link = <strong>{formatMessage({ id: 'setup.setup_add_room_type_and_rooms' })}</strong>
        }

        if (lastUrl === 'finish') {
          link = <strong>{formatMessage({ id: 'setup.finish' })}</strong>
        }

        if (lastUrl === 'fixed-assets' && index === breadcrumbs.length - 1) {
          link = <strong>{menu.fixed_assets}</strong>
        }

        if (match.url.match(/^\/fixed-assets\/(\d)+\/upgrade$/)) {
          const fixedAssetId = match.url.replace('/upgrade', '').replace('/fixed-assets/', '')
          const newUrl = `/fixed-assets/detail/${fixedAssetId}`
          link = (
            <Link key={newUrl} to={newUrl} className="text-muted">
              Upgrade
            </Link>
          )
        }

        if (match.url.match(/fixed-assets\/(\d)+\/upgrade\/detail/)) {
          link = <strong>{formatMessage({ id: 'breadcrumb.detail' })}</strong>
        }

        if (match.url.match(/fixed-assets\/(\d)+\/upgrade\/edit\/(\d)+/)) {
          link = <strong>{formatMessage({ id: 'breadcrumb.edit' })}</strong>
        }

        if (lastUrl === 'lock-date' && index === breadcrumbs.length - 1) {
          link = <strong>{menu.lock_date}</strong>
        }

        if (lastUrl === 'warehouses' && index === breadcrumbs.length - 1) {
          link = <strong>{menu.inventory}</strong>
        }

        if (lastUrl === 'credit-memo') {
          link = <span className="text-muted">{_term.payable}</span>
        }

        if (lastUrl === 'debit-memo') {
          link = <span className="text-muted">{_term.receivable}</span>
        }

        if (lastUrl === 'importStatus') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>Import Status</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                Import Status
              </Link>
            )
          }
        }
        if (match.url === '/invoice-payment-credit-memo') {
          link = (
            <span className="text-muted">
              {formatMessage({
                id: 'invoicePaymentCreditMemo.invoicePaymentCreditMemo',
              })}
            </span>
          )
        }
        if (match.url === '/purchaseinvoice-payment-debit-memo') {
          link = (
            <span className="text-muted">
              {formatMessage({
                id: 'purchaseInvoicePaymentDebitMemo.purchaseInvoicePaymentDebitMemo',
              })}
            </span>
          )
        }
        if (match.url === '/unrealized-gain') {
          link = (
            <span className="text-muted">
              {formatMessage({
                id: 'unrealizedGain.unrealizedGain',
              })}
            </span>
          )
        }

        if (match.url === '/faqs') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>FAQ</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                FAQ
              </Link>
            )
          }
        }

        if (lastUrl === 'fees') {
          link = (
            <strong>
              {menu.fees ||
                formatMessage({
                  id: 'transactionFees.fees',
                })}
            </strong>
          )
        }

        if (lastUrl === 'witholdings') {
          link = <strong>{menu.witholdings || formatMessage({ id: 'witholdings.title' })}</strong>
        }

        if (match.url.includes('/settings/payment-connect/')) {
          link = (
            <strong>
              {paymentConnectProviders.find((provider) => provider.id === Number(lastUrl))?.name ??
                'Provider'}
            </strong>
          )
        }

        if (lastUrl === 'access-per-contact') {
          link = (
            <strong>
              {formatMessage({
                id: 'financeWareHouses.per_contact_warehouse_access',
              })}
            </strong>
          )
        }

        if (match.url === '/reports/product-serial-stock-movement') {
          link = <strong>{reportmenu.inventory_product_serial_stock_movements}</strong>
        }

        if (match.url === '/reports/inventory-product-serial-stock') {
          link = <strong>{reportmenu.inventory_product_serial_stock}</strong>
        }

        if (match.url === '/reports/inventory-product-serial-expires-soon') {
          link = <strong>{reportmenu.inventory_product_serial_expires_soon}</strong>
        }

        if (match.url === '/reports/inventory-product-serial-expiry-age') {
          link = <strong>{reportmenu.inventory_product_serial_expiry_age}</strong>
        }

        if (match.url === '/reports/inventory-production-serial-number-history') {
          link = <strong>{reportmenu.inventory_product_serial_history}</strong>
        }

        if (match.url === '/purchases/purchaseinvoices/import-return') {
          const title = `${formatMessage({ id: 'button.import' })} ${menu.purchase_returns}`
          link = <strong>{title}</strong>
        }

        if (match.url === '/reports/purchase-order-per-product') {
          link = <strong>{reportmenu.purchase_order_per_product}</strong>
        }

        if (match.url === '/reports/credit-memo') {
          link = <strong>{reportmenu.credit_memo}</strong>
        }

        if (match.url === '/reports/debit-memo') {
          link = <strong>{reportmenu.debit_memo}</strong>
        }

        if (match.url === '/reports/balance-receivable-termin') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{reportmenu.balance_receivable_termin}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {reportmenu.balance_receivable_termin}
              </Link>
            )
          }
        }

        if (match.url === '/reports/balance-payable-termin') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{reportmenu.balance_payable_termin}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {reportmenu.balance_payable_termin}
              </Link>
            )
          }
        }

        if (match.url === '/reports/sales-per-region') {
          link = <strong>{reportmenu.sales_per_region}</strong>
        }

        if (match.url === '/reports/purchases-per-region') {
          link = <strong>{reportmenu.purchase_per_region}</strong>
        }

        if (match.url === '/reports/pos-sales-detail') {
          link = <strong>{reportmenu.pos_sales_detail}</strong>
        }

        if (match.url === '/reports/sell-price-history') {
          link = <strong>{reportmenu.product_price_history}</strong>
        }

        if (match.url === '/reports/pricelist-unit-conversion') {
          link = <strong>{reportmenu.pricelist_unit_conversion}</strong>
        }

        if (match.url === '/reports/inventory-turnover') {
          link = <strong>{reportmenu.inventory_turnover}</strong>
        }

        if (match.url === '/reports/disbursment-payment-connect-kledo') {
          link = <strong>{reportmenu.disbursement_payment_connect_kledo}</strong>
        }

        if (match.url === '/reports/transaction-payment-connect-kledo') {
          link = (
            <strong>
              {reportmenu.payment_connect_kledo_transaction ||
                formatMessage({ id: 'report.payment_connect_kledo_transaction' })}
            </strong>
          )
        }
        if (match.url === '/zap') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>Zap</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                Zap
              </Link>
            )
          }
        }

        if (match.url === '/zap/fleets') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{menu.zap_fleet}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {menu.zap_fleet}
              </Link>
            )
          }
        }

        if (match.url === '/zap/routes') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{menu.zap_route}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {menu.zap_route}
              </Link>
            )
          }
        }

        if (match.url === '/zap/canvassing') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{menu.zap_canvassing}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {menu.zap_canvassing}
              </Link>
            )
          }
        }

        if (match.url === '/zap/settings') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{menu.zap_settings}</strong>
          } else {
            link = (
              <Link key={match.url} to={match.url} className="text-muted">
                {menu.zap_settings}
              </Link>
            )
          }
        }

        if (match.url === '/zap/recap-deliveries') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{menu.zap_delivery_recap}</strong>
          } else {
            link = (
              <Link key={match.url} to="/zap/recap-deliveries" className="text-muted">
                {menu.zap_delivery_recap}
              </Link>
            )
          }
        }

        if (match.url === '/zap/recap-invoices') {
          if (index === breadcrumbs.length - 1) {
            link = <strong>{menu.zap_invoice_recap}</strong>
          } else {
            link = (
              <Link key={match.url} to="/zap/recap-invoices" className="text-muted">
                {menu.zap_invoice_recap}
              </Link>
            )
          }
        }

        if (match.url === '/zap/recap-deliveries/confirm') {
          link = <strong>{formatMessage({ id: 'zap.confirmation' })}</strong>
        }

        if (match.path === '/zap/recap-deliveries/confirm/:id') {
          link = ''
        }

        // Handle additional breadcrumbs (from query parameters)
        if (hasAdditionalBreadcrumbs && additionalBreadcrumbId) {
          const matchingBreadcrumb = additionalBreadcrumbs.find(
            (additionalBreadcrumb) => additionalBreadcrumb.id === additionalBreadcrumbId,
          )
          if (matchingBreadcrumb) {
            // If this is the last breadcrumb, make it strong, otherwise make it a link
            if (isLastBreadcrumb) {
              link = <strong key={matchingBreadcrumb.label}>{matchingBreadcrumb.label}</strong>
            } else {
              link = (
                <Link key={match.url} to={match.url} className="text-muted">
                  {matchingBreadcrumb.label}
                </Link>
              )
            }
          }
        }

        if (!link) {
          return null
        }

        // Use unique key: combine URL with additionalBreadcrumbId or index to avoid duplicate keys
        const uniqueKey = additionalBreadcrumbId
          ? `${match.url}-${additionalBreadcrumbId}`
          : match.url

        return (
          <span key={uniqueKey}>
            {countBreadCrumb > 1 && index > 0 && <span className={`${styles.arrow} text-muted`} />}
            {link}
          </span>
        )
      })
      .filter(Boolean)
      .map((breadcrumb, idx) => {
        // Exclude menu awal breadcrumb yang user tidak punya akses seperti beranda
        // Sehingga breadcrumb tidak menampilkan . di awal
        if (idx === 0 && breadcrumb.props.children.length === 2) {
          return {
            ...breadcrumb,
            props: {
              ...breadcrumb.props,
              children: breadcrumb.props.children.map((child, i) => (i === 0 ? false : child)),
            },
          }
        }

        return breadcrumb
      })
  }, [
    _term.payable,
    _term.receivable,
    account,
    billing.can_be_renewed,
    breadcrumbs,
    formatMessage,
    getAccountName,
    getAdditionalBreadcrumbs,
    getProcessActiveKeySettingMenu,
    getWarehouseName,
    isMobileView,
    location,
    menu,
    menuLeftData,
    paymentConnectProviders,
    reportmenu,
    loadedInit,
  ])

  const { show: showBackButton } = useSelector(backButtonSelector)

  return (
    <div
      className={styles.breadcrumbs}
      style={{
        backgroundColor: darkMode ? '#141414' : '#FFFFFF',
        borderBottom: darkMode ? '1px solid #141414' : '1px solid #e4e9f0',
      }}
    >
      {!loadedInit ? (
        <Skeleton.Input style={{ width: 300 }} active />
      ) : (
        <div className={styles.path} style={{ color: darkMode ? '#FFFFFF' : '#141414' }}>
          {renderBreadcrumbs}
        </div>
      )}
      {showBackButton && <Back key="back-button" />}
    </div>
  )
}

export default withBreadcrumbs([
  ...routeConfig,
  // ...setupRoutes,
  ...financeBankRoutes,
])(Breadcrumbs)
